// App-level store for per-user table column widths.
//
// Owns ONE blob: { "<storageKey>": { "<columnId>": <width>, ... }, ... } holding
// every resizable table's layout. It is:
//   - seeded synchronously from a single localStorage cache key (instant paint),
//   - hydrated ONCE from the backend when authenticated (backend is source of truth),
//   - saved back to the backend (debounced) + localStorage on every change.
//
// Each table's ResizableColumnsProvider reads/writes its own slice via useColumnLayouts().
// If this provider isn't mounted, ResizableColumns falls back to per-table localStorage.

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const CACHE_KEY = 'erp:table-column-widths';
const PREF_KEY = 'table-column-widths';
const SAVE_DEBOUNCE_MS = 600;

type WidthMap = Record<string, number>;
type Layouts = Record<string, WidthMap>;

interface ColumnLayoutsContextValue {
  layouts: Layouts;
  getLayout: (storageKey: string) => WidthMap;
  setLayout: (storageKey: string, widths: WidthMap) => void;
}

const ColumnLayoutsContext = createContext<ColumnLayoutsContextValue | null>(null);

function loadCache(): Layouts {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCache(layouts: Layouts) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(layouts));
  } catch {}
}

interface ProviderProps {
  token?: string | null;
  children: React.ReactNode;
}

export function ColumnLayoutsProvider({ token, children }: ProviderProps) {
  const [layouts, setLayouts] = useState<Layouts>(() => loadCache());
  const layoutsRef = useRef(layouts);
  layoutsRef.current = layouts;
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hydrate from backend once per authenticated session. Backend wins for the
  // keys it knows; any local-only keys (set while offline) are preserved.
  useEffect(() => {
    const accessToken = token || localStorage.getItem('accessToken');
    if (!accessToken) return;
    let cancelled = false;
    axios
      .get(`${API}/api/user/preferences/${PREF_KEY}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        if (cancelled) return;
        const serverLayouts: Layouts = (res.data && res.data.value) || {};
        if (!serverLayouts || !Object.keys(serverLayouts).length) return;
        setLayouts((prev) => {
          const merged = { ...prev, ...serverLayouts };
          saveCache(merged);
          return merged;
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  const pushToBackend = useCallback((next: Layouts) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      axios
        .put(
          `${API}/api/user/preferences/${PREF_KEY}`,
          { value: next },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        )
        .catch(() => {});
    }, SAVE_DEBOUNCE_MS);
  }, []);

  // Stable read of the current value (usable inside useState initializers).
  const getLayout = useCallback((storageKey: string): WidthMap => {
    return layoutsRef.current[storageKey] || {};
  }, []);

  const setLayout = useCallback(
    (storageKey: string, widths: WidthMap) => {
      setLayouts((prev) => {
        const next = { ...prev, [storageKey]: widths };
        saveCache(next);
        pushToBackend(next);
        return next;
      });
    },
    [pushToBackend],
  );

  return (
    <ColumnLayoutsContext.Provider value={{ layouts, getLayout, setLayout }}>
      {children}
    </ColumnLayoutsContext.Provider>
  );
}

export function useColumnLayouts(): ColumnLayoutsContextValue | null {
  return useContext(ColumnLayoutsContext);
}
